import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiRequest, fetchUserInfo, teamPhoto } from '../../utils';
import { play } from '../../hooks/useSound';
import Base from '../Base';
import Icon from '../../components/common/Icon';
import './Leaderboard.css';

const formatPoints = (n) => {
  const v = Math.round(Number(n) || 0);
  return v.toLocaleString();
};

// On the overall board a team of the OTHER event (same day, same venue) opens its public page;
// the own event's teams keep the usual /team hop.
const teamLink = (team) => (team.is_own_event === false
  ? { to: `/public-team/${team.id}` }
  : { to: '/team', onClick: () => sessionStorage.setItem('otherTeamId', team.id) });

const EventBadge = ({ team, className }) => (
  team.is_own_event === false && team.event_logo ? (
    <span className={className}><img src={team.event_logo} alt="" /></span>
  ) : null
);

const COMBINED_KEY = 'leaderboard-combined';

const teamImage = (team) => {
  const photo = teamPhoto(team.photo_url, team.company_picture);
  const contain = !team.photo_url && !!team.company_picture;
  return { photo, contain };
};

/* ----------------------- podium slot ----------------------- */

const Slot = ({ team, rank, tone }) => {
  const { photo, contain } = teamImage(team);
  const crownIcon = rank === 1 ? 'crown' : 'medal';

  return (
    <Link {...teamLink(team)} className={`lb-slot lb-slot--${tone}`}>
      <span className="lb-slot__crown">
        <Icon name={crownIcon} pack="sr" />
      </span>

      <div className="lb-slot__avatar-wrap">
        <div
          className={`lb-slot__avatar ${contain ? 'lb-slot__avatar--contain' : ''}`}
          style={{ backgroundImage: `url(${photo})` }}
        />
        {team.is_rival && (
          <span className="lb-slot__rival">
            <Icon name="megaphone" pack="sr" />
          </span>
        )}
        <EventBadge team={team} className="lb-slot__event" />
        <span className="lb-slot__rank">{rank}</span>
      </div>

      <p className="lb-slot__name">{team.name}</p>
      {team.company_name && <p className="lb-slot__company">{team.company_name}</p>}

      <div className="lb-slot__points">
        {formatPoints(team.points)}
        <span className="lb-slot__points-label">pts</span>
      </div>
    </Link>
  );
};

/* ----------------------- list row ----------------------- */

const Row = ({ team, rank, isMine }) => {
  const { photo, contain } = teamImage(team);

  return (
    <Link {...teamLink(team)} className={`lb-row ${isMine ? 'lb-row--mine' : ''}`}>
      {team.house_color && (
        <span className="lb-row__house" style={{ background: team.house_color }} />
      )}

      <div className="lb-row__rank">{rank}</div>

      <div className="lb-row__avatar-wrap">
        <div
          className={`lb-row__avatar ${contain ? 'lb-row__avatar--contain' : ''}`}
          style={{ backgroundImage: `url(${photo})` }}
        />
        {team.is_rival && (
          <span className="lb-row__rival">
            <Icon name="megaphone" pack="sr" />
          </span>
        )}
        <EventBadge team={team} className="lb-row__event" />
      </div>

      <div className="lb-row__body">
        <p className="lb-row__name">{team.name}</p>
        {team.company_name && (
          <p className="lb-row__company">
            {team.house_color && !isMine && (
              <span className="lb-row__house-flag" style={{ background: team.house_color }} />
            )}
            {team.company_name}
          </p>
        )}
      </div>

      <div className="lb-row__points">
        <span className="lb-row__points-num">{formatPoints(team.points)}</span>
        <span className="lb-row__points-label">pts</span>
      </div>
    </Link>
  );
};

/* ----------------------- main ----------------------- */

const Leaderboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [, setError] = useState(null);

  useEffect(() => {
    fetchUserInfo('/leaderboard', setData, setLoading, setError, ['selectedEventId']);
  }, []);

  // The overall board (HT × PLG Dubai): offered by the API only when the organiser switched it on
  // for both events. The choice is remembered per device.
  const offer = data?.combined;
  const [combinedOn, setCombinedOn] = useState(() => {
    try { return localStorage.getItem(COMBINED_KEY) === '1'; } catch { return false; }
  });
  const [combinedData, setCombinedData] = useState(null);
  const [showScaleInfo, setShowScaleInfo] = useState(false);
  const showCombined = !!offer?.available && combinedOn;

  useEffect(() => {
    if (!showCombined || combinedData) return;
    apiRequest('GET', '/leaderboard/combined')
      .then(setCombinedData)
      .catch(() => setCombinedOn(false));
  }, [showCombined, combinedData]);

  const toggleCombined = (on) => {
    setCombinedOn(on);
    try { localStorage.setItem(COMBINED_KEY, on ? '1' : '0'); } catch { /* private mode */ }
  };

  const view = showCombined ? combinedData : data;
  const teams = useMemo(() => view?.teams || [], [view]);
  const myTeamId = data?.user_team_id;
  // Equal totals share a place on the overall board; the own board counts down the list.
  const rankOf = (team, idx) => (showCombined ? team.place : idx + 1);

  // Celebratory chime once, when the standings first reveal.
  const revealedRef = useRef(false);
  useEffect(() => {
    if (!revealedRef.current && !loading && teams.length > 0) {
      revealedRef.current = true;
      play('leaderboard');
    }
  }, [loading, teams.length]);

  const myEntry = useMemo(() => {
    if (!myTeamId || !teams.length) return null;
    const idx = teams.findIndex((tm) => tm.id === myTeamId);
    if (idx < 0) return null;
    return { idx, rank: showCombined ? teams[idx].place : idx + 1, team: teams[idx] };
  }, [teams, myTeamId, showCombined]);

  const top3 = teams.slice(0, 3);
  const rest = teams.slice(3);
  const myInTop3 = myEntry && myEntry.idx < 3;

  return (
    <Base activeNav="home">
      <div className="lb">
        {/* HEADER */}
        <div className="lb-header">
          <button
            className="lb-header__back"
            onClick={() => navigate(-1)}
            aria-label={t('back', { defaultValue: 'Back' })}
          >
            <Icon name="angle-left" />
          </button>
          <h1 className="lb-header__title">{t('leaderboard')}</h1>
        </div>

        {/* OVERALL BOARD TOGGLE — the other event on the same day at the same venue */}
        {offer?.available && (
          <div className="lb-combined">
            <label className="lb-combined__switch">
              <input
                type="checkbox"
                checked={combinedOn}
                onChange={(e) => toggleCombined(e.target.checked)}
              />
              <span className="lb-combined__track" aria-hidden="true" />
              {offer.other_logo && <img src={offer.other_logo} alt="" className="lb-combined__logo" />}
              <span className="lb-combined__label">
                {t(offer.label_key, { event: offer.other_event_name })}
              </span>
            </label>
            <button
              type="button"
              className="lb-combined__info"
              onClick={() => setShowScaleInfo((v) => !v)}
              aria-label={t('combinedScaleInfoLabel')}
            >
              <Icon name="info" />
            </button>
          </div>
        )}
        {offer?.available && showScaleInfo && (
          <div className="lb-notice">
            <Icon name="info" />
            <span>{t('combinedScaleInfo')}</span>
          </div>
        )}

        {/* QUARTER NOTICE */}
        {view?.leaderboard_quarter && (
          <div className="lb-notice">
            <Icon name="info" />
            <span>{t('leaderboardQuarters', { quarter: view.leaderboard_quarter })}</span>
          </div>
        )}

        {/* LOADING */}
        {((loading && !data) || (showCombined && !combinedData)) && (
          <>
            <div className="lb-skel-podium" />
            <div className="lb-skel-row" />
            <div className="lb-skel-row" />
            <div className="lb-skel-row" />
          </>
        )}

        {/* EMPTY */}
        {!loading && view && teams.length === 0 && (
          <div className="lb-empty">{t('noLeaderboardFound')}</div>
        )}

        {/* PODIUM */}
        {top3.length > 0 && (
          <div className="lb-podium">
            <div className="lb-podium__kicker">
              <Icon name="trophy" pack="sr" /> {t('leaderboard')}
            </div>

            <div className="lb-podium__row">
              {top3[1] ? (
                <Slot team={top3[1]} rank={rankOf(top3[1], 1)} tone="silver" />
              ) : (
                <span />
              )}
              {top3[0] && <Slot team={top3[0]} rank={rankOf(top3[0], 0)} tone="gold" />}
              {top3[2] ? (
                <Slot team={top3[2]} rank={rankOf(top3[2], 2)} tone="bronze" />
              ) : (
                <span />
              )}
            </div>
          </div>
        )}

        {/* YOUR TEAM PIN — only when not already in top 3 */}
        {myEntry && !myInTop3 && (
          <Link {...teamLink(myEntry.team)} className="lb-you">
            <div className="lb-you__rank">
              <span className="lb-you__rank-hash">#</span>{myEntry.rank}
            </div>
            <div className="lb-you__body">
              <div className="lb-you__kicker">{t('yourTeam')}</div>
              <p className="lb-you__name">{myEntry.team.name}</p>
              <p className="lb-you__points">
                <strong>{formatPoints(myEntry.team.points)}</strong> {t('points')}
              </p>
              {showCombined && combinedData?.own_place && (
                <p className="lb-you__points">
                  {t('combinedOwnPlace', { place: combinedData.own_place, event: combinedData.own_event_name })}
                </p>
              )}
            </div>
            <span className="lb-you__arrow">
              <Icon name="angle-right" />
            </span>
          </Link>
        )}

        {/* REST OF TABLE */}
        {rest.length > 0 && (
          <>
            {top3.length > 0 && (
              <div className="lb-divider">{t('rank')}</div>
            )}
            <div className="lb-list">
              {rest.map((team, i) => (
                <Row
                  key={team.id}
                  team={team}
                  rank={rankOf(team, i + 3)}
                  isMine={team.id === myTeamId}
                />
              ))}
            </div>
          </>
        )}

        {/* PHOTOS — after the event, the album is the next thing people come for */}
        {data?.show_album && (
          <Link to="/album" className="lb-album">
            <Icon name="images" />
            <span>{t('viewPhotoAlbum')}</span>
            <Icon name="angle-right" />
          </Link>
        )}
      </div>
    </Base>
  );
};

export default Leaderboard;
